/**
 * trainings.ts - Ghost training definitions
 */

import type { GhostId } from './ghosts.js';

export type TrainingId =
  | 'scream_practice'
  | 'jumpscare_acting'
  | 'psychological_acting'
  | 'atmosphere_directing'
  | 'physical_training'
  | 'audience_analysis';

export interface TrainingDef {
  id: TrainingId;
  nameKey: string;
  descKey: string;
  durationMs: number;       // how long the training takes
  costSp: number;           // SP cost to start training
  scareBonus: number;       // permanent scare bonus after completion (multiplied by ghost level)
  expReward: number;        // exp rewarded on completion
  fatigueReduction: number; // fatigue reduction on completion (can be 0)
}

/** Training-in-progress log messages per ghost (comedy flavor) */
export interface TrainingQuote {
  ghostId: GhostId;
  trainingId: TrainingId;
  quoteKey: string;  // i18n key for the funny in-progress quote
}

export const TRAINING_DEFS: Record<TrainingId, TrainingDef> = {
  scream_practice: {
    id: 'scream_practice',
    nameKey: 'training_scream_practice',
    descKey: 'training_scream_practice_desc',
    durationMs: 30000,     // 30s
    costSp: 50,
    scareBonus: 0.05,
    expReward: 30,
    fatigueReduction: 0,
  },
  jumpscare_acting: {
    id: 'jumpscare_acting',
    nameKey: 'training_jumpscare_acting',
    descKey: 'training_jumpscare_acting_desc',
    durationMs: 60000,     // 60s
    costSp: 100,
    scareBonus: 0.08,
    expReward: 50,
    fatigueReduction: 0,
  },
  psychological_acting: {
    id: 'psychological_acting',
    nameKey: 'training_psychological_acting',
    descKey: 'training_psychological_acting_desc',
    durationMs: 90000,     // 90s
    costSp: 150,
    scareBonus: 0.10,
    expReward: 70,
    fatigueReduction: 0,
  },
  atmosphere_directing: {
    id: 'atmosphere_directing',
    nameKey: 'training_atmosphere_directing',
    descKey: 'training_atmosphere_directing_desc',
    durationMs: 120000,    // 120s
    costSp: 200,
    scareBonus: 0.12,
    expReward: 90,
    fatigueReduction: 0,
  },
  physical_training: {
    id: 'physical_training',
    nameKey: 'training_physical_training',
    descKey: 'training_physical_training_desc',
    durationMs: 45000,     // 45s
    costSp: 80,
    scareBonus: 0.06,
    expReward: 40,
    fatigueReduction: 10,
  },
  audience_analysis: {
    id: 'audience_analysis',
    nameKey: 'training_audience_analysis',
    descKey: 'training_audience_analysis_desc',
    durationMs: 60000,     // 60s
    costSp: 120,
    scareBonus: 0.07,
    expReward: 60,
    fatigueReduction: 0,
  },
};

export const ALL_TRAINING_IDS = Object.keys(TRAINING_DEFS) as TrainingId[];

/**
 * Funny training quotes: each ghost has a unique comedy line per training type.
 * These are i18n keys that resolve to localized strings.
 */
export const TRAINING_QUOTES: TrainingQuote[] = [
  // scream_practice
  { ghostId: 'jinaeryeong', trainingId: 'scream_practice', quoteKey: 'tq_jinaeryeong_scream' },
  { ghostId: 'gangsi', trainingId: 'scream_practice', quoteKey: 'tq_gangsi_scream' },
  { ghostId: 'vampire', trainingId: 'scream_practice', quoteKey: 'tq_vampire_scream' },
  { ghostId: 'kappa', trainingId: 'scream_practice', quoteKey: 'tq_kappa_scream' },
  { ghostId: 'pierrot', trainingId: 'scream_practice', quoteKey: 'tq_pierrot_scream' },
  { ghostId: 'banshee', trainingId: 'scream_practice', quoteKey: 'tq_banshee_scream' },
  { ghostId: 'mummy', trainingId: 'scream_practice', quoteKey: 'tq_mummy_scream' },
  { ghostId: 'werewolf', trainingId: 'scream_practice', quoteKey: 'tq_werewolf_scream' },
  // jumpscare_acting
  { ghostId: 'jinaeryeong', trainingId: 'jumpscare_acting', quoteKey: 'tq_jinaeryeong_jumpscare' },
  { ghostId: 'gangsi', trainingId: 'jumpscare_acting', quoteKey: 'tq_gangsi_jumpscare' },
  { ghostId: 'vampire', trainingId: 'jumpscare_acting', quoteKey: 'tq_vampire_jumpscare' },
  { ghostId: 'kappa', trainingId: 'jumpscare_acting', quoteKey: 'tq_kappa_jumpscare' },
  { ghostId: 'pierrot', trainingId: 'jumpscare_acting', quoteKey: 'tq_pierrot_jumpscare' },
  { ghostId: 'banshee', trainingId: 'jumpscare_acting', quoteKey: 'tq_banshee_jumpscare' },
  { ghostId: 'mummy', trainingId: 'jumpscare_acting', quoteKey: 'tq_mummy_jumpscare' },
  { ghostId: 'werewolf', trainingId: 'jumpscare_acting', quoteKey: 'tq_werewolf_jumpscare' },
  // psychological_acting
  { ghostId: 'jinaeryeong', trainingId: 'psychological_acting', quoteKey: 'tq_jinaeryeong_psych' },
  { ghostId: 'gangsi', trainingId: 'psychological_acting', quoteKey: 'tq_gangsi_psych' },
  { ghostId: 'vampire', trainingId: 'psychological_acting', quoteKey: 'tq_vampire_psych' },
  { ghostId: 'kappa', trainingId: 'psychological_acting', quoteKey: 'tq_kappa_psych' },
  { ghostId: 'pierrot', trainingId: 'psychological_acting', quoteKey: 'tq_pierrot_psych' },
  { ghostId: 'banshee', trainingId: 'psychological_acting', quoteKey: 'tq_banshee_psych' },
  { ghostId: 'mummy', trainingId: 'psychological_acting', quoteKey: 'tq_mummy_psych' },
  { ghostId: 'werewolf', trainingId: 'psychological_acting', quoteKey: 'tq_werewolf_psych' },
  // atmosphere_directing
  { ghostId: 'jinaeryeong', trainingId: 'atmosphere_directing', quoteKey: 'tq_jinaeryeong_atmo' },
  { ghostId: 'gangsi', trainingId: 'atmosphere_directing', quoteKey: 'tq_gangsi_atmo' },
  { ghostId: 'vampire', trainingId: 'atmosphere_directing', quoteKey: 'tq_vampire_atmo' },
  { ghostId: 'kappa', trainingId: 'atmosphere_directing', quoteKey: 'tq_kappa_atmo' },
  { ghostId: 'pierrot', trainingId: 'atmosphere_directing', quoteKey: 'tq_pierrot_atmo' },
  { ghostId: 'banshee', trainingId: 'atmosphere_directing', quoteKey: 'tq_banshee_atmo' },
  { ghostId: 'mummy', trainingId: 'atmosphere_directing', quoteKey: 'tq_mummy_atmo' },
  { ghostId: 'werewolf', trainingId: 'atmosphere_directing', quoteKey: 'tq_werewolf_atmo' },
  // physical_training
  { ghostId: 'jinaeryeong', trainingId: 'physical_training', quoteKey: 'tq_jinaeryeong_physical' },
  { ghostId: 'gangsi', trainingId: 'physical_training', quoteKey: 'tq_gangsi_physical' },
  { ghostId: 'vampire', trainingId: 'physical_training', quoteKey: 'tq_vampire_physical' },
  { ghostId: 'kappa', trainingId: 'physical_training', quoteKey: 'tq_kappa_physical' },
  { ghostId: 'pierrot', trainingId: 'physical_training', quoteKey: 'tq_pierrot_physical' },
  { ghostId: 'banshee', trainingId: 'physical_training', quoteKey: 'tq_banshee_physical' },
  { ghostId: 'mummy', trainingId: 'physical_training', quoteKey: 'tq_mummy_physical' },
  { ghostId: 'werewolf', trainingId: 'physical_training', quoteKey: 'tq_werewolf_physical' },
  // audience_analysis
  { ghostId: 'jinaeryeong', trainingId: 'audience_analysis', quoteKey: 'tq_jinaeryeong_audience' },
  { ghostId: 'gangsi', trainingId: 'audience_analysis', quoteKey: 'tq_gangsi_audience' },
  { ghostId: 'vampire', trainingId: 'audience_analysis', quoteKey: 'tq_vampire_audience' },
  { ghostId: 'kappa', trainingId: 'audience_analysis', quoteKey: 'tq_kappa_audience' },
  { ghostId: 'pierrot', trainingId: 'audience_analysis', quoteKey: 'tq_pierrot_audience' },
  { ghostId: 'banshee', trainingId: 'audience_analysis', quoteKey: 'tq_banshee_audience' },
  { ghostId: 'mummy', trainingId: 'audience_analysis', quoteKey: 'tq_mummy_audience' },
  { ghostId: 'werewolf', trainingId: 'audience_analysis', quoteKey: 'tq_werewolf_audience' },
];

export function getTrainingQuote(ghostId: GhostId, trainingId: TrainingId): string | null {
  const found = TRAINING_QUOTES.find(
    (q) => q.ghostId === ghostId && q.trainingId === trainingId,
  );
  return found?.quoteKey ?? null;
}
